/** Session storage key for deep-link redirect after admin login. */
export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

/** Session storage keys for practice context from need-attention email deep link token. */
export const APPOINTMENTS_NEED_ATTENTION_GROUP_KEY =
  'appointmentsNeedAttentionClinicGroupId';
export const APPOINTMENTS_NEED_ATTENTION_CLINIC_KEY =
  'appointmentsNeedAttentionClinicId';

export type NeedAttentionDeepLinkPayload = {
  clinic_group_id: number;
  clinic_id: number;
};

export const encodeNeedAttentionDeepLinkToken = (
  payload: NeedAttentionDeepLinkPayload
): string => {
  const json = JSON.stringify({
    clinic_group_id: Number(payload.clinic_group_id),
    clinic_id: Number(payload.clinic_id)
  });
  return btoa(json).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const decodeNeedAttentionDeepLinkToken = (
  token: string
): NeedAttentionDeepLinkPayload | null => {
  if (!token || typeof token !== 'string') {
    return null;
  }
  try {
    const normalized = token.replace(/-/g, '+').replace(/_/g, '/');
    const pad =
      normalized.length % 4 === 0
        ? ''
        : '='.repeat(4 - (normalized.length % 4));
    const json = atob(normalized + pad);
    const data = JSON.parse(json);
    // eslint-disable-next-line camelcase
    const clinic_group_id = Number(data?.clinic_group_id);
    // eslint-disable-next-line camelcase
    const clinic_id = Number(data?.clinic_id);
    if (
      !Number.isFinite(clinic_group_id) ||
      !Number.isFinite(clinic_id) ||
      // eslint-disable-next-line camelcase
      clinic_group_id <= 0 ||
      // eslint-disable-next-line camelcase
      clinic_id <= 0
    ) {
      return null;
    }
    // eslint-disable-next-line camelcase
    return {clinic_group_id, clinic_id};
  } catch (e) {
    return null;
  }
};

export const saveNeedAttentionDeepLinkContext = (
  payload: NeedAttentionDeepLinkPayload
): void => {
  try {
    sessionStorage.setItem(
      APPOINTMENTS_NEED_ATTENTION_GROUP_KEY,
      String(payload.clinic_group_id)
    );
    sessionStorage.setItem(
      APPOINTMENTS_NEED_ATTENTION_CLINIC_KEY,
      String(payload.clinic_id)
    );
  } catch (e) {
    // Ignore storage failures
  }
};

export const getNeedAttentionDeepLinkContext = (): NeedAttentionDeepLinkPayload | null => {
  try {
    // eslint-disable-next-line camelcase
    const clinic_group_id = Number(
      sessionStorage.getItem(APPOINTMENTS_NEED_ATTENTION_GROUP_KEY)
    );
    // eslint-disable-next-line camelcase
    const clinic_id = Number(
      sessionStorage.getItem(APPOINTMENTS_NEED_ATTENTION_CLINIC_KEY)
    );
    if (
      !Number.isFinite(clinic_group_id) ||
      !Number.isFinite(clinic_id) ||
      // eslint-disable-next-line camelcase
      clinic_group_id <= 0 ||
      // eslint-disable-next-line camelcase
      clinic_id <= 0
    ) {
      return null;
    }
    // eslint-disable-next-line camelcase
    return {clinic_group_id, clinic_id};
  } catch (e) {
    return null;
  }
};

export const clearNeedAttentionDeepLinkContext = (
  options: {keepClinic?: boolean; keepGroup?: boolean} = {}
): void => {
  try {
    if (!options.keepGroup) {
      sessionStorage.removeItem(APPOINTMENTS_NEED_ATTENTION_GROUP_KEY);
    }
    if (!options.keepClinic) {
      sessionStorage.removeItem(APPOINTMENTS_NEED_ATTENTION_CLINIC_KEY);
    }
  } catch (e) {
    // Ignore storage failures
  }
};

/** Read clinic id from deep link without removing it. */
export const peekNeedAttentionClinicId = (): number | null => {
  try {
    // eslint-disable-next-line camelcase
    const clinic_id = Number(
      sessionStorage.getItem(APPOINTMENTS_NEED_ATTENTION_CLINIC_KEY)
    );
    // eslint-disable-next-line camelcase
    if (!Number.isFinite(clinic_id) || clinic_id <= 0) {
      return null;
    }
    // eslint-disable-next-line camelcase
    return clinic_id;
  } catch (e) {
    return null;
  }
};

/** Remove clinic id after it has been applied to the practice selector. */
export const clearNeedAttentionClinicId = (): void => {
  try {
    sessionStorage.removeItem(APPOINTMENTS_NEED_ATTENTION_CLINIC_KEY);
  } catch (e) {
    // Ignore storage failures
  }
};

export const consumeNeedAttentionClinicId = (): number | null => {
  // eslint-disable-next-line camelcase
  const clinic_id = peekNeedAttentionClinicId();
  // eslint-disable-next-line camelcase
  if (clinic_id) {
    clearNeedAttentionClinicId();
  }
  // eslint-disable-next-line camelcase
  return clinic_id;
};

/** Capture need-attention email token from a URL search string into session storage. */
export const captureNeedAttentionDeepLinkFromSearch = (
  search: string
): NeedAttentionDeepLinkPayload | null => {
  try {
    const params = new URLSearchParams(search || '');
    const token = params.get('token');
    if (!token) {
      return getNeedAttentionDeepLinkContext();
    }
    const payload = decodeNeedAttentionDeepLinkToken(token);
    if (payload) {
      saveNeedAttentionDeepLinkContext(payload);
    }
    return payload;
  } catch (e) {
    return null;
  }
};

export const savePostLoginRedirect = (pathWithSearch: string): void => {
  if (!pathWithSearch || typeof pathWithSearch !== 'string') {
    return;
  }
  const normalized = pathWithSearch.trim();
  if (!normalized || normalized === '/' || normalized.startsWith('/login')) {
    return;
  }
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, normalized);
  } catch (e) {
    // Ignore storage failures (private mode, etc.)
  }
};

/**
 * Consume a stored post-login redirect.
 * Returns pathname without leading slash (for auth initialRoute),
 * and persists the appointments need-attention clinic context when a token is present.
 */
export const consumePostLoginRedirect = (): {
  route: string;
  deepLink: NeedAttentionDeepLinkPayload | null;
} | null => {
  try {
    const stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (!stored) {
      return null;
    }
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);

    const url = new URL(stored, window.location.origin);
    const deepLink = captureNeedAttentionDeepLinkFromSearch(url.search);

    return {
      route: url.pathname.replace(/^\//, ''),
      deepLink
    };
  } catch (e) {
    return null;
  }
};
